// @givmo/disclosure — the terms panel: the approved presentation of a disclosure (D-CAM-44).
//
// Lifted from the employer portal's `ChargeDisclosureTerms` (`@givmo/employer-module`
// `components/charge-disclosure.tsx`), which Jonathan approved on the portal's Give and approve
// dialogs (D-CAM-41 → D-CAM-44), so the surfaces that rendered the boxed `DisclosureBlock` card take
// the same treatment (CAM-R60): the Connect payment sheet, the gift-card purchase page, donate's P2P
// team and supporter pages, and the portal's gift-card send dialogs.
//   - a heading over the text, as a real heading, with a hairline rule beside it. Its words are the
//     caller's, never registry copy: "Terms" on every one of those surfaces (D-CAM-97);
//   - the FULL text, never collapsed, as flat fine print in a tinted panel bounded to five lines;
//   - a drawn thin scrollbar, always visible while there is more text, and a bottom fade over the
//     last visible line that clears at the end (macOS and phones hide an idle native scrollbar);
//   - no words cue the scroll (D-CAM-36), and nothing covers any line: every byte is in the DOM and
//     reachable by scrolling or the keyboard.
// The lines render VERBATIM, one unbulleted `<li>` per line — the gift-card e2e specs compare the
// rendered `li` texts with the live registry row, line for line. Nothing here edits registry copy
// or the echo.
//
// `appearance` picks the tokens: `saas` is the portal's (the approved panel's classes, with the two
// fixes below); `legacy` is the donate and pay palette, built only from tokens a Connect partner's
// dark appearance flips (`black`, `gray20`, `gray30`, `gray40`, `gray80`), so the payment sheet's
// themes carry through. The fine print clears 4.5:1 in both: `legacy` is `gray80` at 75% (#696969 on
// #f5f5f5, 5.0:1; #a9abad on #26292c in the dark scope, 6.3:1 — the donate fine print's 70% measures
// 4.4:1 on the tint); `saas` is `neutral-600` at 90% (#5c6674 on `surface-sunken` #f8fafc, 5.56:1),
// where the approved panel's `text-tertiary` measures 4.47:1 on that fill, a known gap of the design
// system's token-contrast floor (cut 25 review F2).
//
// The panel is a keyboard stop, so it draws a focus indicator in each palette's own idiom: the
// portal's house pairing (`FOCUS_RING` in the design system's `lib/focus.ts`: the brand ring PLUS a
// 2px solid `brand-500` outline, 4.66:1 on white — the ring alone composites to 1.23:1, cut 25 review
// F1), and on donate and pay the solid 2px blue outline their links draw, which clears 3:1 on the
// sheet in both themes (pay's contrast guard pins it). Each outline re-arms its style
// (`outline-solid`) because the region's base `outline-none` would otherwise disarm it
// (agent_docs/build_and_ci.md, the variable-driven traps).
//
// SSR (donate and pay serve a nonce-only `style-src`, so an inline `style` attribute in server markup
// is refused): nothing here puts one there. The five-line bound is a class; the line-snapped bound,
// the drawn track and the thumb are applied only after mount, through the CSSOM, which that policy
// allows.

import {
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

/** One body to show: its lines, and — only when a panel shows more than one — who it applies to. */
export interface DisclosureTermsSection {
  /** A stable React key (the body's content hash, or a fixed name for a single body). */
  key: string;
  /** Who this body applies to, or `null` when it is the panel's only body. */
  label: string | null;
  lines: readonly string[];
}

export interface DisclosureTermsProps {
  /** The heading's words, which also name the scroll region. Client-owned, never registry copy. */
  heading: string;
  sections: readonly DisclosureTermsSection[];
  /** The block root's `data-testid`; the region, fade, track and thumb derive `-region` etc. */
  testId: string;
  /** `saas` — the employer portal's tokens; `legacy` — the donate and pay palette. */
  appearance?: 'saas' | 'legacy';
  /** The heading's level: 2 on a page, 3 inside a dialog whose title is the h2. */
  headingLevel?: 2 | 3;
  /**
   * `data-*` attributes for the block root, beside its `data-testid`: a caller's test-facing marker
   * of WHICH body is shown (the portal's gift-card notices name their surface and `content_hash`,
   * GC-FU12b). Never registry copy and never read by this panel.
   */
  dataAttributes?: Readonly<Record<`data-${string}`, string>>;
}

const CLASSES = {
  saas: {
    heading: 'font-sans text-s12 font-w600 leading-h15 tracking-normal text-text-secondary',
    rule: 'bg-border-subtle',
    region: 'rounded-lg border border-border-subtle bg-surface-sunken',
    sectionLabel: 'font-sans text-s12 font-w600 leading-[1.45] tracking-normal text-text-secondary',
    line: 'font-sans text-s12 font-w400 leading-[1.45] tracking-normal text-neutral-600/90',
    fade: 'rounded-bl-lg from-surface-sunken',
    track: 'bg-border',
    thumb: 'bg-text-tertiary/45',
    focus:
      'focus-visible:shadow-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
  },
  legacy: {
    heading: 'font-sans text-s12 font-w600 leading-h15 tracking-givmo text-black',
    rule: 'bg-gray40',
    region: 'rounded-all12 border border-gray30 bg-gray20',
    sectionLabel: 'font-sans text-s12 font-w600 leading-[1.45] tracking-normal text-black',
    line: 'font-sans text-s12 font-w400 leading-[1.45] tracking-normal text-gray80/75',
    fade: 'rounded-bl-all12 from-gray20',
    track: 'bg-gray40',
    thumb: 'bg-gray80/40',
    focus:
      'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue',
  },
} as const;

/** More text lies below the region's visible box (one-pixel tolerance for fractional layout). */
function hasMoreBelow(scrollTop: number, clientHeight: number, scrollHeight: number): boolean {
  return scrollTop + clientHeight < scrollHeight - 1;
}

interface ScrollMetrics {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

const NO_METRICS: ScrollMetrics = { scrollTop: 0, clientHeight: 0, scrollHeight: 0 };

/**
 * The region's scroll geometry, re-read on scroll and whenever the region or its content changes
 * size (a new body, a font landing, a width change), so the fade and the thumb always describe the
 * text actually on screen.
 */
function useScrollMetrics(ref: RefObject<HTMLDivElement | null>, content: unknown) {
  const [metrics, setMetrics] = useState<ScrollMetrics>(NO_METRICS);
  const measure = useCallback(() => {
    const element = ref.current;
    if (!element) return;
    const next = {
      scrollTop: element.scrollTop,
      clientHeight: element.clientHeight,
      scrollHeight: element.scrollHeight,
    };
    setMetrics((previous) =>
      previous.scrollTop === next.scrollTop &&
      previous.clientHeight === next.clientHeight &&
      previous.scrollHeight === next.scrollHeight
        ? previous
        : next,
    );
  }, [ref]);

  useLayoutEffect(() => {
    measure();
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    for (const child of Array.from(element.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, ref, content]);

  return { metrics, measure };
}

/** The panel's nominal height: its border, top padding and five 17.4px lines (`max-h-[99px]`). */
const PANEL_MAX_PX = 99;

/**
 * The panel's bottom edge snaps to a whole line. At the nominal 99px it falls between the fifth and
 * sixth lines of one paragraph, but a body of several paragraphs (6px apart) can lay a line across
 * that edge, which would then show only the tops of its glyphs under the fade. The box grows just
 * enough to hold that line whole, so its first view still ends on a line. `null` keeps the class
 * bound: the server render, and every body whose edge already falls between lines.
 */
function useLineSnappedMaxHeight(
  ref: RefObject<HTMLDivElement | null>,
  content: unknown,
): number | null {
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const region = ref.current;
    if (!region) return;
    const snap = () => {
      const border = region.offsetHeight - region.clientHeight;
      const edge = PANEL_MAX_PX - border;
      // Line positions from the padding box's top at scroll 0, so a scrolled panel measures the same.
      const origin = region.getBoundingClientRect().top + region.clientTop - region.scrollTop;
      let next: number | null = null;
      for (const paragraph of Array.from(region.querySelectorAll('[data-disclosure-line]'))) {
        const lineHeight = parseFloat(getComputedStyle(paragraph).lineHeight);
        if (!Number.isFinite(lineHeight) || lineHeight <= 0) continue;
        const rect = paragraph.getBoundingClientRect();
        const lines = Math.round(rect.height / lineHeight);
        for (let line = 0; line < lines; line += 1) {
          const top = rect.top - origin + line * lineHeight;
          const bottom = top + lineHeight;
          if (top < edge - 0.5 && bottom > edge + 0.5) next = Math.ceil(bottom) + border;
        }
      }
      setMaxHeight(next);
    };
    snap();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(snap);
    observer.observe(region);
    return () => observer.disconnect();
  }, [ref, content]);
  return maxHeight;
}

/** The drawn scrollbar's inset from the region's top and bottom edges, and its thumb's floor. */
const TRACK_INSET_PX = 8;
const MIN_THUMB_PX = 20;

/**
 * The disclosure under its heading, in a tinted, rounded panel bounded to five lines of fine print
 * (99px: its border, top padding and five 17.4px lines, so the edge falls between lines; a body of
 * spaced paragraphs snaps the edge to a whole line, see `useLineSnappedMaxHeight`), the rest by
 * scrolling.
 *
 * THE BOX MUST LOOK SCROLLABLE AT A GLANCE (Jonathan, 2026-09-25, on the portal's second mock: "it
 * just looks like cut off text"). The region hides the native scrollbar everywhere and draws its own:
 * a thin track and a thumb, always visible while there is more text than the box holds, sized to the
 * share on screen and placed at the scroll position (a pointer can drag it or click the track). A
 * bottom fade into the panel's fill sits over the last visible line while more text lies below and
 * clears at the end, so the last line then reads in full.
 *
 * The region is a keyboard stop named by the heading (`role="region"`, `aria-labelledby`,
 * `tabIndex`), so arrow and Page keys scroll it, with the palette's focus indicator for keyboard
 * focus. Renders nothing when there are no lines.
 */
export function DisclosureTerms({
  heading,
  sections,
  testId,
  appearance = 'saas',
  headingLevel = 2,
  dataAttributes,
}: DisclosureTermsProps) {
  const shown = sections.filter((section) => section.lines.length > 0);
  const classes = CLASSES[appearance];
  const headingId = useId();
  const regionRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  // Re-measure when the bodies change: their keys, and the lines themselves (a resolve can replace a
  // floor under the same key).
  const contentKey = shown
    .map((section) => `${section.key}:${section.label ?? ''}:${section.lines.join('\n')}`)
    .join('|');
  const { metrics, measure } = useScrollMetrics(regionRef, contentKey);
  const snappedMaxHeight = useLineSnappedMaxHeight(regionRef, contentKey);
  const drag = useRef<{ pointerId: number; startY: number; startScrollTop: number } | null>(null);

  const { scrollTop, clientHeight, scrollHeight } = metrics;
  const overflowing = scrollHeight - clientHeight > 1;
  const moreBelow = overflowing && hasMoreBelow(scrollTop, clientHeight, scrollHeight);
  const trackHeight = Math.max(0, clientHeight - 2 * TRACK_INSET_PX);
  const thumbHeight = overflowing
    ? Math.min(trackHeight, Math.max(MIN_THUMB_PX, (trackHeight * clientHeight) / scrollHeight))
    : 0;
  const maxScroll = Math.max(1, scrollHeight - clientHeight);
  const thumbTop = overflowing ? ((trackHeight - thumbHeight) * scrollTop) / maxScroll : 0;

  // Dragging the thumb scrolls the region by the same share of its travel; a click on the track
  // (outside the thumb) centres the thumb there.
  const scrollPerPixel = trackHeight > thumbHeight ? maxScroll / (trackHeight - thumbHeight) : 0;
  const onThumbPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    if (!region) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drag.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startScrollTop: region.scrollTop,
    };
  };
  const onThumbPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    const state = drag.current;
    if (!region || state === null || state.pointerId !== event.pointerId) return;
    region.scrollTop = state.startScrollTop + (event.clientY - state.startY) * scrollPerPixel;
    measure();
  };
  const onThumbPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current?.pointerId === event.pointerId) drag.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };
  const onTrackPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const region = regionRef.current;
    const track = trackRef.current;
    if (!region || !track) return;
    const offset = event.clientY - track.getBoundingClientRect().top - thumbHeight / 2;
    region.scrollTop = offset * scrollPerPixel;
    measure();
  };

  if (shown.length === 0) return null;
  const Heading = headingLevel === 3 ? 'h3' : 'h2';

  return (
    <div className="flex w-full flex-col gap-8 text-left" {...dataAttributes} data-testid={testId}>
      <div className="flex items-center gap-10">
        <Heading id={headingId} className={classes.heading}>
          {heading}
        </Heading>
        <span aria-hidden className={`h-px flex-1 ${classes.rule}`} />
      </div>
      <div className="relative">
        <div
          ref={regionRef}
          className={`max-h-[99px] space-y-12 overflow-y-auto py-10 pr-20 pl-12 outline-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${classes.region} ${classes.focus}`}
          style={snappedMaxHeight === null ? undefined : { maxHeight: snappedMaxHeight }}
          role="region"
          aria-labelledby={headingId}
          tabIndex={0}
          onScroll={measure}
          data-testid={`${testId}-region`}
        >
          {shown.map((section) => (
            // No gap under a label: it sits on the same 17.4px line grid as the text.
            <div key={section.key} className="flex flex-col">
              {section.label !== null && (
                <p className={classes.sectionLabel} data-disclosure-line>
                  {section.label}
                </p>
              )}
              <ul className="flex flex-col">
                {section.lines.map((line, index) => (
                  <li
                    key={index}
                    className={`${classes.line} ${index > 0 ? 'mt-6' : ''}`}
                    data-disclosure-line
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* The fade over the last visible line while more text lies below — into the panel's fill,
            inside its border, never over the drawn scrollbar. */}
        <div
          aria-hidden
          className={`pointer-events-none absolute right-20 bottom-px left-px h-28 bg-gradient-to-t from-15% to-transparent transition-opacity duration-150 ${classes.fade} ${
            moreBelow ? 'opacity-100' : 'opacity-0'
          }`}
          data-visible={moreBelow ? 'true' : 'false'}
          data-testid={`${testId}-fade`}
        />
        {overflowing && (
          <div
            ref={trackRef}
            aria-hidden
            className={`absolute right-6 w-4 rounded-full ${classes.track}`}
            style={{ top: TRACK_INSET_PX, height: trackHeight }}
            onPointerDown={onTrackPointerDown}
            data-testid={`${testId}-track`}
          >
            <div
              className={`absolute inset-x-0 cursor-grab touch-none rounded-full active:cursor-grabbing ${classes.thumb}`}
              style={{ top: thumbTop, height: thumbHeight }}
              onPointerDown={onThumbPointerDown}
              onPointerMove={onThumbPointerMove}
              onPointerUp={onThumbPointerUp}
              onPointerCancel={onThumbPointerUp}
              data-testid={`${testId}-thumb`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
